/**
 * Fish Statistics Utilities
 * Handles fish stat calculations, conversions, and quality determination
 */

import { Quality, FishRarity } from '@/types';

// === UNIT CONVERSIONS ===

/**
 * Convert pounds to ounces
 */
export function poundsToOunces(pounds: number): number {
  return pounds * 16;
}

/**
 * Convert ounces to pounds (as decimal)
 */
export function ouncesToPounds(ounces: number): number {
  return ounces / 16;
}

/**
 * Convert ounces to pounds and ounces (for display)
 * Returns [pounds, ounces]
 */
export function ouncesToPoundsAndOunces(totalOunces: number): [number, number] {
  const pounds = Math.floor(totalOunces / 16);
  const ounces = totalOunces % 16;
  return [pounds, ounces];
}

/**
 * Convert inches to centimeters
 */
export function inchesToCm(inches: number): number {
  return inches * 2.54;
}

/**
 * Convert centimeters to inches
 */
export function cmToInches(cm: number): number {
  return cm / 2.54;
}

/**
 * Format weight for display
 * @param ounces - Total weight in ounces
 * @returns "X lbs Y oz" or "Y oz" if less than 1 lb
 */
export function formatWeight(ounces: number): string {
  const [lbs, oz] = ouncesToPoundsAndOunces(ounces);
  if (lbs === 0) {
    return `${oz} oz`;
  }
  if (oz === 0) {
    return `${lbs} lb${lbs !== 1 ? 's' : ''}`;
  }
  return `${lbs} lb${lbs !== 1 ? 's' : ''} ${oz} oz`;
}

/**
 * Format length for display
 * @param inches - Length in inches
 * @param showMetric - Whether to show metric conversion
 * @returns "X.XX in" or "X.XX in (Y.Y cm)"
 */
export function formatLength(inches: number, showMetric: boolean = false): string {
  const rounded = Math.round(inches * 4) / 4; // Quarter-inch precision
  if (showMetric) {
    const cm = Math.round(inchesToCm(inches) * 10) / 10;
    return `${rounded.toFixed(2)}" (${cm} cm)`;
  }
  return `${rounded.toFixed(2)}"`;
}

// === QUALITY CALCULATION ===

/**
 * Calculate fish quality based on stats
 *
 * Algorithm:
 * 1. Calculate length percentile (how close to max length)
 * 2. Calculate weight percentile (how close to max weight)
 * 3. Factor in health (0-1)
 * 4. Apply species rarity floor
 * 5. Map combined score to Quality tier
 *
 * @param length - Fish length in inches
 * @param weight - Fish weight in ounces
 * @param health - Fish health (0-1, where 1.0 is perfect)
 * @param lengthRange - Species length range [min, max] in inches
 * @param weightRange - Species weight range [min, max] in ounces
 * @param speciesRarity - Base rarity of species (affects minimum quality)
 * @returns Quality tier
 */
export function calculateFishQuality(
  length: number,
  weight: number,
  health: number,
  lengthRange: [number, number],
  weightRange: [number, number],
  speciesRarity: FishRarity,
  qualityBonus: number = 0
): Quality {
  // Calculate percentiles (0-1)
  const [minLength, maxLength] = lengthRange;
  const [minWeight, maxWeight] = weightRange;

  const lengthPercentile = (length - minLength) / (maxLength - minLength);
  const weightPercentile = (weight - minWeight) / (maxWeight - minWeight);

  // Weighted average: length 40%, weight 40%, health 20%
  // This gives roughly equal importance to size metrics while health provides bonus
  const rawScore = (lengthPercentile * 0.4) + (weightPercentile * 0.4) + (health * 0.2);

  // Apply species rarity floor bonus
  // Rare species have higher minimum quality
  const rarityBonus = {
    [FishRarity.COMMON]: 0.0,     // No bonus
    [FishRarity.UNCOMMON]: 0.05,  // +5% base score (Uncommon more likely)
    [FishRarity.RARE]: 0.10,      // +10% base score (Rare more likely)
    [FishRarity.EPIC]: 0.15,      // +15% base score (Epic more likely)
    [FishRarity.LEGENDARY]: 0.20, // +20% base score (Legendary more likely)
  }[speciesRarity] || 0;

  // Final score with rarity floor
  const finalScore = Math.min(1.0, rawScore + rarityBonus + qualityBonus);

  // Map score to quality tiers
  // Common: 0.00 - 0.50 (50% - most common)
  // Uncommon: 0.50 - 0.70 (20% - fairly common)
  // Rare: 0.70 - 0.85 (15% - uncommon)
  // Epic: 0.85 - 0.95 (10% - rare)
  // Legendary: 0.95 - 0.999 (4.9% - very rare)
  // Unique: 0.999 - 1.00 (0.1% - extremely rare, 1 in 1000)
  // Quest: Never rolled naturally (0%)
  if (finalScore < 0.50) {
    return Quality.COMMON;
  } else if (finalScore < 0.70) {
    return Quality.UNCOMMON;
  } else if (finalScore < 0.85) {
    return Quality.RARE;
  } else if (finalScore < 0.95) {
    return Quality.EPIC;
  } else if (finalScore < 0.999) {
    return Quality.LEGENDARY;
  } else {
    return Quality.UNIQUE; // Top 0.1% - extremely rare perfect catches
  }
}

/**
 * Roll fish length within species range
 * Uses weighted distribution favoring middle sizes (bell curve)
 * @param minLength - Minimum length in inches
 * @param maxLength - Maximum length in inches
 * @returns Length in inches (quarter-inch granularity)
 */
export function rollFishLength(minLength: number, maxLength: number): number {
  // Use Box-Muller transform to generate normal distribution
  // This creates a bell curve centered around the midpoint
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);

  // Map z-score (-3 to 3) to our range
  // Most values will cluster around the middle
  const midpoint = (minLength + maxLength) / 2;
  const range = (maxLength - minLength) / 6; // 6 sigma = full range

  const rawLength = midpoint + (z * range);

  // Clamp to range and round to quarter-inch
  const clampedLength = Math.max(minLength, Math.min(maxLength, rawLength));
  return Math.round(clampedLength * 4) / 4;
}

/**
 * Roll fish weight based on length
 * Uses length-weight relationship (weight increases cubically with length)
 * @param length - Fish length in inches
 * @param lengthRange - Species length range [min, max]
 * @param weightRange - Species weight range [min, max] in ounces
 * @returns Weight in ounces (whole ounce granularity)
 */
export function rollFishWeight(
  length: number,
  lengthRange: [number, number],
  weightRange: [number, number]
): number {
  const [minLength, maxLength] = lengthRange;
  const [minWeight, maxWeight] = weightRange;

  // Calculate length percentile
  const lengthPercentile = (length - minLength) / (maxLength - minLength);

  // Weight scales cubically with length (realistic fish growth)
  const weightPercentile = Math.pow(lengthPercentile, 3);

  // Calculate weight with some random variation (±10%)
  const baseWeight = minWeight + (weightPercentile * (maxWeight - minWeight));
  const variation = baseWeight * 0.1;
  const randomWeight = baseWeight + ((Math.random() - 0.5) * 2 * variation);

  // Clamp to range and round to whole ounce
  return Math.round(Math.max(minWeight, Math.min(maxWeight, randomWeight)));
}

/**
 * Roll fish health
 * Future: Will be affected by equipment, skills, etc.
 * For now, returns perfect health (1.0)
 * @param isWild - Whether fish is wild (wild fish have slightly better health)
 * @returns Health value (0-1)
 */
export function rollFishHealth(isWild: boolean = true): number {
  // TODO: Factor in equipment quality, skill, etc.
  // Base health is 1.0 for wild fish, 0.9 for hatchery fish
  return isWild ? 1.0 : 0.9;
}

/**
 * Determine if a fish should be wild or hatchery-raised
 * Only applies to species with canBeHatchery = true
 * 70% chance of wild, 30% chance of hatchery
 * @param canBeHatchery - Whether this species can be hatchery-raised
 * @returns True if wild, false if hatchery
 */
export function rollIsWild(canBeHatchery: boolean = false): boolean {
  if (!canBeHatchery) {
    return true; // Species that can't be hatchery are always wild
  }
  return Math.random() < 0.7; // 70% wild, 30% hatchery
}

/**
 * Apply wild fish bonus to length
 * Wild fish grow slightly larger
 * @param baseLength - Base length in inches
 * @param isWild - Whether fish is wild
 * @returns Adjusted length
 */
export function applyWildLengthBonus(baseLength: number, isWild: boolean): number {
  return isWild ? baseLength * 1.05 : baseLength; // +5% for wild
}

/**
 * Apply wild fish bonus to weight
 * Wild fish weigh slightly more
 * @param baseWeight - Base weight in ounces
 * @param isWild - Whether fish is wild
 * @returns Adjusted weight (rounded to whole ounces)
 */
export function applyWildWeightBonus(baseWeight: number, isWild: boolean): number {
  return isWild ? Math.round(baseWeight * 1.05) : baseWeight; // +5% for wild, rounded to whole oz
}
